import { getAuthorizedFetchOptions } from '../../../helpers/getAuthorizedFetchOptions';
import { CaseSummary, ExpectedReturnSource } from './caseSummary';

export interface CaseTiming {
  expectedReturnDate?: string;
  expectedReturnSource?: ExpectedReturnSource;
}

export interface CaseTimingService {
  /** Due dates keyed by case id. Never throws: anything that goes wrong yields an empty map. */
  fetch(caseIds: string[]): Promise<Map<string, CaseTiming>>;
}

export interface CreateCaseTimingServiceOptions {
  endpoint?: string;
  fetchImpl?: typeof fetch;
  logger?: Pick<Console, 'warn'>;
  timeoutMs?: number;
}

/** The API accepts at most this many ids per call. */
export const MAX_IDS_PER_REQUEST = 50;
const DEFAULT_TIMEOUT_MS = 8000;
const SOURCES: ExpectedReturnSource[] = ['scheduled', 'tat', 'standard'];

const asTiming = (row: any): CaseTiming | null => {
  if (!row || typeof row.caseId !== 'string') return null;
  const date = typeof row.expectedReturnDate === 'string' && row.expectedReturnDate ? row.expectedReturnDate : undefined;
  const source = SOURCES.includes(row.expectedReturnSource) ? (row.expectedReturnSource as ExpectedReturnSource) : undefined;
  return date ? { expectedReturnDate: date, expectedReturnSource: source } : {};
};

/**
 * Asks `REACT_APP_CHAT_CASE_TIMING_END_POINT` (POST api/chat/case-timing) when the loaded cases are due.
 * With no endpoint configured it answers with an empty map, so Evie simply says nothing about due dates.
 */
export function createCaseTimingService({
  endpoint = process.env.REACT_APP_CHAT_CASE_TIMING_END_POINT || '',
  fetchImpl,
  logger = console,
  timeoutMs = DEFAULT_TIMEOUT_MS,
}: CreateCaseTimingServiceOptions = {}): CaseTimingService {
  const doFetch = fetchImpl || (typeof fetch !== 'undefined' ? fetch.bind(globalThis) : undefined);
  const target = (endpoint || '').trim();

  const fetchBatch = async (caseIds: string[]): Promise<Map<string, CaseTiming>> => {
    const result = new Map<string, CaseTiming>();
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : undefined;
    try {
      const options = { ...getAuthorizedFetchOptions('POST', { caseIds }), signal: controller?.signal } as RequestInit;
      const response = await (doFetch as typeof fetch)(target, options);
      if (!response.ok) {
        logger.warn(`[askAi] case timing request failed (HTTP ${response.status})`);
        return result;
      }
      const rows = await response.json();
      (Array.isArray(rows) ? rows : []).forEach((row) => {
        const timing = asTiming(row);
        if (timing) result.set(String(row.caseId).toLowerCase(), timing);
      });
    } catch (error) {
      logger.warn('[askAi] case timing request failed', error);
    } finally {
      if (timer) clearTimeout(timer);
    }
    return result;
  };

  return {
    async fetch(caseIds) {
      const ids = Array.from(new Set(caseIds.map((id) => String(id || '').trim()).filter(Boolean)));
      if (!target || !doFetch || ids.length === 0) return new Map();
      const batches: string[][] = [];
      for (let i = 0; i < ids.length; i += MAX_IDS_PER_REQUEST) batches.push(ids.slice(i, i + MAX_IDS_PER_REQUEST));
      const maps = await Promise.all(batches.map(fetchBatch));
      const merged = new Map<string, CaseTiming>();
      maps.forEach((m) => m.forEach((v, k) => merged.set(k, v)));
      return merged;
    },
  };
}

/** Copies the due dates onto the matching cases; cases the API did not return are left untouched. */
export async function withCaseTiming(cases: CaseSummary[], timing: CaseTimingService): Promise<CaseSummary[]> {
  if (cases.length === 0) return cases;
  const byId = await timing.fetch(cases.map((c) => c.id));
  if (byId.size === 0) return cases;
  return cases.map((c) => {
    const t = byId.get(String(c.id).toLowerCase());
    return t ? { ...c, expectedReturnDate: t.expectedReturnDate, expectedReturnSource: t.expectedReturnSource } : c;
  });
}

/** Service used when due dates are not wired in. */
export const noopCaseTimingService: CaseTimingService = {
  async fetch() {
    return new Map();
  },
};
